import { TableCell, TableRow, useTheme } from "@mui/material";
import { Track } from "../../../interfaces/dtos/track"
import { ConfigurationsListItemSwitchInput } from "./ConfigurationsListItemSwitchInput";
import { useAppDispatch } from "../../../redux/hooks";
import { saveTrack } from "../../../redux/track/trackAction";
import { useFeedback } from "@sis/react-library";
import { dispatchIsSuccessful } from "../../../utils/commonUtils";
import { CommonCanUpdate, CommonCanUpdateDesc } from "../../../utils/commonRules";

interface ConfigurationsListItemProps {
    track: Track;
    readOnly: boolean;
}

export const ConfigurationsListItem = ({
    track, readOnly
}: ConfigurationsListItemProps) => {

    const theme = useTheme();
    const dispatch = useAppDispatch();
    const { showSuccess } = useFeedback();

    const handleChange = async (fieldName: keyof Track, value: boolean) => {
        const updatedTrack = { ...track, [fieldName]: value };
        // If EP display is false, EP distribute must be false.
        if (fieldName === "isEPDisplay" && !value) { updatedTrack.isEPDistribute = false; }
        // If BP display is false, BP distribute must be false.
        if (fieldName === "isBPDisplay" && !value) { updatedTrack.isBPDistribute = false; }
        // If Distribute is set to true, then display should also be set to true
        if (fieldName === "isEPDistribute" && value) { updatedTrack.isEPDisplay = true; }
        if (fieldName === "isBPDistribute" && value) { updatedTrack.isBPDisplay = true; }

        const result = await dispatch(saveTrack(updatedTrack));
        const success = dispatchIsSuccessful(result);
        if (success) {
            showSuccess("Configuration was updated successfully.");
        }
    }

    const isUKandIRHorses = track.categoryCode === "HR" && (track.countryCode === 'UK' || track.countryCode === 'IE');

    const epDisplayProps = () => {
        // Check if you can update the track
        const reason = canUpdateTrackReason("EP");
        return {
            readOnly: readOnly || (reason !== undefined),
            tooltip: reason
        }
    }

    const bpDisplayProps = () => {
        // Firstly check if you can update the track
        let reason = canUpdateTrackReason("BP");
        // If BP, can't edit UK & IR Horses
        if (!reason && isUKandIRHorses) {
            reason = "Can't configure BP for UK & Ireland Horses";
        }
        return {
            readOnly: readOnly || (reason !== undefined),
            tooltip: reason
        }
    }

    const canUpdateTrackReason = (priceType: string) => {
        const canUpdateTrack = CommonCanUpdate(track.provider, track.categoryCode);
        return canUpdateTrack ? undefined : `Can't configure ${priceType} for ${CommonCanUpdateDesc}`;
    }

    const props = { track, readOnly, onChange: handleChange };

    return (
        <TableRow id={`configurations-listitem-${track.key}`}>
            <TableCell sx={{ color: track.isTrackActive ? theme.palette.success.main : theme.palette.warning.main }}>
                {track.isTrackActive ? 'Active' : 'Inactive'}
            </TableCell>
            <TableCell>{track.categoryCode}</TableCell>
            <TableCell>{track.shortDescription}</TableCell>
            <TableCell>{track.sportCode}</TableCell>
            <TableCell>{track.countryCode}</TableCell>
            <TableCell>
                <ConfigurationsListItemSwitchInput {...props} {...epDisplayProps()} fieldName="isEPDisplay" />
            </TableCell>
            <TableCell>
                <ConfigurationsListItemSwitchInput {...props} {...epDisplayProps()} fieldName="isEPDistribute" />
            </TableCell>
            <TableCell>
                <ConfigurationsListItemSwitchInput {...props}  {...bpDisplayProps()} fieldName="isBPDisplay" />
            </TableCell>
            <TableCell>
                <ConfigurationsListItemSwitchInput {...props} {...bpDisplayProps()} fieldName="isBPDistribute" />
            </TableCell>
        </TableRow>
    )
}
